<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, nextTick, computed } from 'vue'
import type { PropType } from 'vue'
import { useI18n } from '@/plugins/i18n'

// @ts-ignore
import vClickOutside from 'click-outside-vue3'

export type TDropDownOption = { id: string; text: string, count?: number }
// emits
const emit = defineEmits(['handleHeaderClick', 'updateSelectedItems', 'handleClickOutside']);

//props
const props = defineProps({
    multiple: { type: Boolean,  default: false, required: false }, 
    name: { type: String,  default: '', required: true },
    placeholder: { type: String,  default: '', required: true },
    placeholderPrefix: { type: String, default: '', required: false },
    searchPlaceholder: { type: String, default: 'Search...', required: false },
    disabled: { type: Boolean, default: false },
    searchable: { type: Boolean, default: false },
    hasClear: { type: Boolean, default: true }, //show or hide the clear all option
    tagging: { type: Boolean, default: false }, //not used yet
    options: { type:  Array as PropType<TDropDownOption[]>, default: () => [], required: false },
    isOpen: { type: Boolean, default: false, required: false }, //set the inital open/close state of dropdown
    iconMain: { type: String, default: '', required: false }, //icon to appear on left side of dropdown
    iconOpenMain: { type: String, default: '', required: false }, //open state icon on left side of dropdown
    iconOpen: { type: String, default: '', required: false }, //open state icon on right side of dropdown
    iconClose: { type: String, default: '', required: false },//closed state icon on right side of dropdown
    iconSelected: { type: String, default: '', required: false },//icon to show when item is selected on the left side of dropdown item
    showSelectCountInHeader: { type: Boolean, default: true, required: false }, //show selected count in header
    value: { type: Array as PropType<TDropDownOption[]>, default: () => [], required: false }, //dropdown selected items
    isLoading: { type: Boolean, default: false, required: false }, //show loading state
    className: { type: String, default: '', required: false }, //additional class name for dropdown to customize the display of dropdown
    showMoreLink: { type: Boolean, default: true, required: false }, //show more link to show more options in dropdown,
    closeOnClickOutside: { type: Boolean, default: false, required: false }, //close dropdown on click outside
    useTeleport: { type: Boolean, default: false, required: false }, //use teleport to render dropdown in body
    closeOnSelect: { type: Boolean, default: false, required: false }, //close dropdown on select
    showSelectedInPlaceholder: { type: Boolean, default: false, required: false }, //show selected items in placeholder
    translate: { type: Boolean, default: false, required: false }, //whether to translate options text,
})



//ref
const el = ref<HTMLElement | null>(null)  //component root element
const activeOptions = ref<TDropDownOption[]>(props.options || []);
const orginalOptions = ref<TDropDownOption[]>(props.options || []); //for search reset
const selectedItems = ref<TDropDownOption[]>(props.value || []);
const searchText = ref('');
const showMoreOptions = ref<Boolean>(false);
const showOptions = ref<Boolean>(props.isOpen || false);
const ready = ref<Boolean>((props.isOpen && !props.useTeleport) || false);
const dropdownStyles = ref<{ [key: string]: string }>({});
const listenersAttached = ref(false) //Attach listeners only once per component instance 

//translations
const i18nInstance: any = useI18n()
const { t } = i18nInstance


const isSelectedItem = (item: TDropDownOption) => {
    return selectedItems.value.findIndex((i: TDropDownOption) => i.id.toString() === item.id.toString()) > -1;
}

const getOptionText = (item: TDropDownOption) => {
  return props.translate ? t(item.text) : item.text
}

//computed
const placeholderText = computed(() => {
  if (props.showSelectedInPlaceholder && selectedItems.value.length > 0) {
    const text = selectedItems.value.map(item => getOptionText(item)).join(', ');
    if (text && props.placeholderPrefix) {
      return `${props.placeholderPrefix} ${text}`;
    }
    return text;
  }
  return props.placeholder;
});

const isDisabled = computed(() => props.disabled)

//event handlers
const handleClearAll = () => {
    selectedItems.value = [];
    emit('updateSelectedItems', selectedItems.value);
}

const handleSelectItem = (item: TDropDownOption) => {
    //add new item to current Collection, if exists remove it
    const index = selectedItems.value.findIndex((i: TDropDownOption) => i.id.toString() === item.id.toString());
    if (index > -1) {
        selectedItems.value.splice(index, 1); // remove item from selection
        emit('updateSelectedItems', selectedItems.value);
        return;
    }
    //if not exists add item to current selection
    if(props.multiple) {
        //if multiple selection is allowed, append item to current selection]
        selectedItems.value.push(item);
        emit('updateSelectedItems', selectedItems.value);
        return;
    } else {
        selectedItems.value = [item];
        emit('updateSelectedItems', selectedItems.value);
    }
    if(props.closeOnSelect) {
        showOptions.value = false;
    }
}

const handleShowMoreOptions = () => {
    showMoreOptions.value = !showMoreOptions.value;
}

const handleHeaderClick = async (show: Boolean) => {
  if(!isDisabled.value) {
    showOptions.value = show;
    emit('handleHeaderClick', show);
  } 
}

const handleSearchOptions = () => {
    if (searchText.value.trim() === '') {
        activeOptions.value = [...orginalOptions.value]; // reset to original options
    } else {
        activeOptions.value = orginalOptions.value.filter(option => 
            getOptionText(option).toLowerCase().includes(searchText.value.toLowerCase())
        );
    }
}

const handleClickOutside = () => {
  if(props.closeOnClickOutside) {
    showOptions.value = false;
    emit('handleClickOutside');
  }
}

// Watches
watch(() => props.isOpen, (newVal) => {
  showOptions.value = newVal
})

watch(() => props.value, (newVal) => {
    selectedItems.value = newVal || [];
}, { immediate: true })

// Update whenever props.options changes
watch(
  () => props.options,
  (newVal) => {
    activeOptions.value = newVal || [];
    orginalOptions.value = [...activeOptions.value]; // store original options for reset
  },
  { immediate: true } // also run this on initial setup
);


const repositionDropdown = () => {
  if (props.useTeleport && showOptions.value && ready.value && el.value) {
        const rect = el.value.getBoundingClientRect()
        const bodyPaddingTop = parseInt(getComputedStyle(document.body).paddingTop, 10) || 0
        dropdownStyles.value = {
            position: 'absolute',
            top: `${rect.bottom + window.scrollY - bodyPaddingTop}px`,
            left: `${rect.left + window.scrollX}px`,
            width: `${rect.width}px`,
            zIndex: '100000'
        }
    }
}

const addListeners = () => {
  if (!listenersAttached.value) {
    window.addEventListener('scroll', repositionDropdown, true)
    window.addEventListener('resize', repositionDropdown, true)
    listenersAttached.value = true
  }
}

const removeListeners = () => {
  if (listenersAttached.value) {
    window.removeEventListener('scroll', repositionDropdown, true)
    window.removeEventListener('resize', repositionDropdown, true)
    listenersAttached.value = false
  }
}


//livecycle hooks
onUnmounted(() => {
  removeListeners()
})

onMounted(() => {
  watch(showOptions, async (val) => {
    if (props.useTeleport) {
      if (val) {
        ready.value = false
        await nextTick()
        if (el.value) {
          ready.value = true
          repositionDropdown()
          addListeners()
        }
      } else {
        removeListeners()
      }
    } else {
      ready.value = val
    }
  })
})



//directives
const vClickOutsideAction = vClickOutside.directive

</script>
<template>
    <div ref="el" :id="`saidropdown-${name}`" :class="['sai-multiselect', showOptions ? 'open' : 'closed', props.className]"  v-click-outside-action="handleClickOutside">
        <div :class="['sai-multiselect--header', showOptions ? 'open' : 'closed', isLoading ? 'loading' : '']" @click="handleHeaderClick(!showOptions)">
            <img v-if="props.iconMain && !showOptions" class="icon-left" :src="props.iconMain" />
            <img v-if="props.iconOpenMain && showOptions" class="icon-left" :src="props.iconOpenMain" />
            <div :class="['sai-multiselect--placeholder', props.showSelectedInPlaceholder ? 'showSelectedInPlaceholder' : '']" :title="placeholderText">{{ placeholderText }}</div>
            <div v-if="props.showSelectCountInHeader && !props.showSelectedInPlaceholder" class="sai-multiselect--count">
                <div v-show="selectedItems.length">{{ selectedItems.length }}</div>
            </div>
            <slot name="info"></slot>
            <img class="icon-right" v-if="showOptions && props.iconOpen" :src="props.iconOpen" />
            <img class="icon-right" v-if="!showOptions && props.iconClose" :src="props.iconClose" />
        </div>
        <teleport to="body" :disabled="!props.useTeleport">
            <div :class="['sai-multiselect--options-spacing-wrapper', props.className]">
              <div :class="['sai-multiselect--options-wrapper', props.className]"   :style="[dropdownStyles]" v-show="showOptions && !isLoading && ready">
                  <div v-if="hasClear" class="sai-multiselect--clear" v-show="activeOptions.length">
                      <div class="selected_count">{{ selectedItems.length }} {{ t('selected') }}</div><div class="clear_selected" @click="handleClearAll">{{ t('clearAll') }}</div>
                  </div>
                  <div v-if="searchable" class="sai-multiselect--search">
                      <input type="text" v-model="searchText" :placeholder="props.searchPlaceholder" @input="handleSearchOptions"/>
                  </div>
                  <div class="sai-multiselect--options">
                      <ul v-if="showMoreLink">
                          <li v-show="activeOptions.length === 0" class="noResult-item">{{ t('noResultFound') }}.</li>
                          <!-- loop till 10 -->
                          <li :class="[ isSelectedItem(item) ? 'selected' : '']" v-for="(item, index) in activeOptions.slice(0, 10)" @click.stop="handleSelectItem(item)">
                              <img class="mark-selected" v-if="props.iconSelected" :src="props.iconSelected" /><div>{{ getOptionText(item) }}</div><div class="count" v-if="item.count">{{ item.count }}</div>
                          </li>
                          <li v-show="activeOptions.length > 10 && !showMoreOptions" class="show-more-options" @click.stop="handleShowMoreOptions">{{ t('more') }}</li>
                          <li v-show="showMoreOptions" :class="[ isSelectedItem(item) ? 'selected' : '']" v-for="(item, index) in activeOptions.slice(10)" @click.stop="handleSelectItem(item)">
                              <img class="mark-selected" v-if="props.iconSelected" :src="props.iconSelected" /><div>{{ getOptionText(item) }}</div><div class="count" v-if="item.count">{{ item.count }}</div>
                          </li>
                          <li v-show="activeOptions.length > 10 && showMoreOptions" class="show-less-options" @click.stop="handleShowMoreOptions">{{ t('less') }}</li>
                      </ul>
                      <ul v-else>
                          <li v-show="activeOptions.length === 0" class="noResult-item">{{ t('noResultFound') }}.</li>
                          <li :class="[ isSelectedItem(item) ? 'selected' : '']" v-for="(item, index) in activeOptions" @click.stop="handleSelectItem(item)">
                              <img class="mark-selected" v-if="props.iconSelected" :src="props.iconSelected" /><div>{{ getOptionText(item) }}</div><div class="count" v-if="item.count">{{ item.count }}</div>
                          </li>
                      </ul>
                  </div>
              </div>
            </div>
        </teleport>
    </div>
</template>
